import apiClient from "./client.js";

/**
 * Fetch all monitors for the active workspace.
 * @param {{ cursor?: string, limit?: number }} [params]
 */
export async function fetchMonitors(params = {}) {
  const res = await apiClient.get("/monitors", { params });
  return res.data; // { data: [], meta: { next_cursor } }
}

export async function fetchMonitorTemplates() {
  const res = await apiClient.get("/monitor-templates");
  return res.data.data;
}

/**
 * Fetch available agent locations.
 * @returns {Array<{ slug: string, label: string }>}
 */
export async function fetchLocations() {
  const res = await apiClient.get("/locations");
  return res.data.data;
}

/**
 * Create a new monitor.
 * @param {{ name: string, type: string, target: string, interval_seconds?: number, locations?: string[] }} data
 */
export async function createMonitor(data) {
  const res = await apiClient.post("/monitors", data);
  return res.data.data;
}

/**
 * Fetch a single monitor by ID.
 * @param {string} id
 */
export async function fetchMonitor(id) {
  const res = await apiClient.get(`/monitors/${id}`);
  return res.data.data;
}

/**
 * Partially update a monitor.
 * @param {string} id
 * @param {Partial<{ name: string, target: string, interval_seconds: number, locations: string[], is_active: boolean }>} data
 */
export async function updateMonitor(id, data) {
  const res = await apiClient.patch(`/monitors/${id}`, data);
  return res.data.data;
}

/**
 * Delete a monitor.
 * @param {string} id
 */
export async function deleteMonitor(id) {
  await apiClient.delete(`/monitors/${id}`);
}

/**
 * Fetch time-series check results for a monitor.
 * @param {string} id
 * @param {{ from_time?: string, to_time?: string, limit?: number }} [params]
 * @returns {{ points: Array, uptime_pct: number, avg_response_ms: number|null, total_checks: number }}
 */
export async function fetchMonitorCheckResults(id, params = {}) {
  const res = await apiClient.get(`/monitors/${id}/check-results`, { params });
  return res.data.data;
}

/**
 * Fetch grouped check runs for a monitor.
 * Each run contains per-location results for the same scheduled check.
 * @param {string} id
 * @param {{ limit?: number, before?: string }} [params]
 * @returns {{ data: Array, meta: { next_cursor: string|null } }}
 */
export async function fetchMonitorChecks(id, params = {}) {
  const res = await apiClient.get(`/monitors/${id}/checks`, { params });
  return res.data;
}

/**
 * Fetch screenshot-history captures for a monitor (newest first).
 * @param {string} id
 * @param {{ viewport?: "desktop"|"mobile", limit?: number }} [params]
 * @returns {Promise<Array<{id: string, captured_at: string, viewport: string, width: number, height: number, url: string}>>}
 */
export async function fetchMonitorScreenshots(id, params = {}) {
  const res = await apiClient.get(`/monitors/${id}/screenshots`, { params });
  return res.data.data;
}

/**
 * Trigger an immediate one-off check for a monitor.
 * @param {string} id
 * @returns {{ job_id: string }}
 */
export async function triggerMonitorCheck(id) {
  const res = await apiClient.post(`/monitors/${id}/check`);
  return res.data.data;
}

/**
 * Fetch aggregated uptime/response-time buckets for a monitor.
 * @param {string} monitorId
 * @param {{ bucket?: string, from_time?: string, to_time?: string }} [params]
 * @returns {Array<{bucket: string, check_count: number, uptime_pct: number|null, p50_ms: number|null, p95_ms: number|null, p99_ms: number|null}>}
 */
export async function fetchUptimeBuckets(monitorId, params = {}) {
  const res = await apiClient.get(`/monitors/${monitorId}/uptime-buckets`, { params });
  return res.data.data;
}

// ----- Visual change baselines -----

export async function fetchVisualBaseline(monitorId) {
  const res = await apiClient.get(`/monitors/${monitorId}/visual-baseline`, {
    validateStatus: (s) => s < 500,
  });
  if (res.status === 404) return null;
  return res.data.data;
}

export async function approveVisualBaseline(monitorId) {
  const res = await apiClient.post(`/monitors/${monitorId}/visual-baseline/approve`);
  return res.data.data;
}

export async function rejectVisualBaseline(monitorId) {
  const res = await apiClient.post(`/monitors/${monitorId}/visual-baseline/reject`);
  return res.data.data;
}

export async function resetVisualBaseline(monitorId) {
  await apiClient.delete(`/monitors/${monitorId}/visual-baseline`);
}

export async function fetchCheckDiagnosis(monitorId, checkId) {
  const res = await apiClient.get(`/monitors/${monitorId}/checks/${checkId}/diagnosis`);
  return res.data.data;
}
